// Classify whether a busy dev port is occupied by OUR OWN managed server or by a
// foreign process — so `ss dev detect`/`status`/`summary` never mistake the
// project's own healthy servers (from a previous `ss dev start`) for a conflict,
// and — crucially — never paint a FOREIGN server that grabbed our port as "ours".
//
// WHY THIS EXISTS (and why the matching changed):
// `ss dev start` launches each server via the WMI service (`Win32_Process.Create`)
// and only ever got back the WRAPPER `cmd.exe` PID — never the `dotnet`/`node`
// child that actually binds the port. The old code therefore matched "is this
// ours?" purely by PORT (`record.port === port`), with ZERO liveness check. That
// is wrong in the case that matters: if our server dies and a DIFFERENT process
// grabs the same port, a port-only match still says "ours" → the idempotent
// shortcut fires and `summary` reports a foreign server as `✓ up`.
//
// THE FIX: `ss dev start` now polls `getListeningPid(port)` right after launch and
// records the REAL listening child PID (`listenPid`) in the sidecar
// (`dev-run.pids.json`). Ownership is then a STRONG IDENTITY check: a busy port is
// ours iff the PID currently listening IS the `listenPid` we captured. When we
// never captured it (slow boot, or a legacy v1 record), the caller may opt into a
// PORT fallback (`allowPortFallback`) — used only by `detect` for its conflict
// classification (worst case: a missed port-change prompt), NEVER by status/summary
// (whose worst case would be painting a stranger "up").
//
// Pure — no I/O — so it is unit-testable. The CLI reads the sidecar + live PIDs and
// takes the netstat snapshot (see parseListeningPorts below).

export interface SidecarRecord {
  /** The dotnet/node child that actually bound this port (captured post-launch). null = never captured. */
  listenPid: number | null;
  /** The port this side was launched on. */
  port: number;
  /** The cmd.exe wrapper PID from Win32_Process.Create — for tree-kill, NOT for identity. */
  wrapperPid?: number;
}

export interface SidecarPids {
  backend?: SidecarRecord;
  frontend?: SidecarRecord;
}

export interface OccupancyInput {
  apiPort: number;
  webPort: number;
  /** PID currently LISTENING on apiPort, or null if the port is free. */
  apiBusyPid: number | null;
  /** PID currently LISTENING on webPort, or null if the port is free. */
  webBusyPid: number | null;
  /** Contents of dev-run.pids.json (the servers WE launched), or null. */
  pids: SidecarPids | null;
  /**
   * When the captured `listenPid` is null (slow boot / legacy v1 record), accept a
   * PORT match as "ours". Default false (strict identity). `detect` passes true so a
   * just-launched env isn't misread as a foreign conflict; status/summary stay strict.
   */
  allowPortFallback?: boolean;
}

export interface OccupancyResult {
  /** apiPort is busy AND the listener is our managed backend. */
  apiBusyBySelf: boolean;
  /** webPort is busy AND the listener is our managed frontend. */
  webBusyBySelf: boolean;
  /** At least one side is busy with a process that is NOT one of ours → a real conflict. */
  occupiedByOther: boolean;
  /** At least one side is busy and all busy sides are ours (idempotent reuse). */
  occupiedBySelf: boolean;
}

function isSelf(
  busyPid: number | null,
  record: SidecarRecord | undefined,
  port: number,
  allowPortFallback: boolean
): boolean {
  if (busyPid === null) return false;
  if (!record) return false;
  // STRONG identity: the live listener IS the child we captured at launch. The
  // wrapper PID is never a listener, so this can only ever match the real server.
  if (record.listenPid !== null && record.listenPid === busyPid) return true;
  // FALLBACK (opt-in): we never captured the child (slow boot / legacy record).
  // A port match is the best we have. Used only where a false "ours" is harmless.
  if (record.listenPid === null && allowPortFallback && record.port === port) return true;
  return false;
}

/**
 * Decide, for the detected api/web ports, whether each busy port is ours and
 * whether any busy port belongs to a foreign process.
 */
export function classifyOccupancy(input: OccupancyInput): OccupancyResult {
  const fallback = input.allowPortFallback === true;
  const apiBusyBySelf = isSelf(input.apiBusyPid, input.pids?.backend, input.apiPort, fallback);
  const webBusyBySelf = isSelf(input.webBusyPid, input.pids?.frontend, input.webPort, fallback);

  const apiBusyByOther = input.apiBusyPid !== null && !apiBusyBySelf;
  const webBusyByOther = input.webBusyPid !== null && !webBusyBySelf;

  return {
    apiBusyBySelf,
    webBusyBySelf,
    occupiedByOther: apiBusyByOther || webBusyByOther,
    occupiedBySelf: (apiBusyBySelf || webBusyBySelf) && !apiBusyByOther && !webBusyByOther,
  };
}

/**
 * Parse the output of `netstat -ano` into a port → listening-PID map. Pure (the
 * caller runs netstat once per CLI invocation and feeds the text here, instead of
 * spawning a full netstat per port lookup).
 *
 * Only LISTENING TCP rows are kept. `\S+:` greedily consumes through the LAST
 * colon, so IPv4 (`127.0.0.1:5142`), wildcard (`0.0.0.0:445`) and IPv6 (`[::]:135`,
 * `[::1]:5142`) all yield the trailing port. First-write-wins per port — preserving
 * the "return the first match" semantics of the previous per-port netstat scan
 * (a dev server dual-binds IPv4+IPv6 with the SAME child PID, so this is a no-op
 * there; it only matters for an unrelated dual-stack listener).
 */
export function parseListeningPorts(netstatOutput: string): Map<number, number> {
  const map = new Map<number, number>();
  for (const line of netstatOutput.split('\n')) {
    const m = line.match(/TCP\s+\S+:(\d+)\s+\S+\s+LISTENING\s+(\d+)/i);
    if (!m) continue;
    const port = parseInt(m[1], 10);
    const pid = parseInt(m[2], 10);
    if (!Number.isNaN(port) && !Number.isNaN(pid) && !map.has(port)) map.set(port, pid);
  }
  return map;
}
